import { useQuery, ApolloError } from "@apollo/client";
import {
  COMPANY_SPEND_BY_COUNTRY_QUERY,
  COMPANY_SPEND_BY_CATEGORY_QUERY,
  COMPANY_AI_SPEND_BY_CATEGORY_QUERY,
  CompanySpendByCountryResponse,
  CompanySpendByCategoryResponse,
} from "../../../legacy/queries/Companies";
import { COMPANY_SEARCH_CLIENT } from "../../../utils/consts";

export enum SpendQueryType {
  BY_COUNTRY = "BY_COUNTRY",
  BY_CATEGORY = "BY_CATEGORY",
  AI_BY_CATEGORY = "AI_BY_CATEGORY",
}

interface SpendQueryFilters {
  categoryParentIds?: string[];
  categoryIds?: string[];
  hgIds?: string[];
  hgIdsIncludingCorporateRelatives?: string[];
}

type SpendResponse =
  | CompanySpendByCountryResponse
  | CompanySpendByCategoryResponse;

interface BaseSpendOptions {
  companyId: string | undefined;
  per?: number;
  skip?: boolean;
}

interface CountrySpendOptions extends BaseSpendOptions {
  queryType: SpendQueryType.BY_COUNTRY;
  filters?: SpendQueryFilters;
}

interface CategorySpendOptions extends BaseSpendOptions {
  queryType: SpendQueryType.BY_CATEGORY | SpendQueryType.AI_BY_CATEGORY;
  filters?: SpendQueryFilters;
}

type UseCompanySpendOptions = CountrySpendOptions | CategorySpendOptions;

interface UseCompanySpendResult {
  data: SpendResponse["spend"] | undefined;
  loading: boolean;
  error: ApolloError | undefined;
  hasSpendData: boolean;
}

export const useCompanySpend = (
  options: UseCompanySpendOptions
): UseCompanySpendResult => {
  const {
    companyId,
    queryType,
    filters = {},
    per = 10,
    skip = false,
  } = options;

  // Select query and build variables based on type
  const getQueryConfig = () => {
    switch (queryType) {
      case SpendQueryType.BY_COUNTRY:
        return {
          query: COMPANY_SPEND_BY_COUNTRY_QUERY,
          variables: {
            hgIdsIncludingCorporateRelatives: companyId,
            categoryParentIds: filters.categoryParentIds || ["0"],
            per,
            ...filters,
          },
        };
      case SpendQueryType.BY_CATEGORY:
      case SpendQueryType.AI_BY_CATEGORY:
        return {
          query:
            queryType === SpendQueryType.AI_BY_CATEGORY
              ? COMPANY_AI_SPEND_BY_CATEGORY_QUERY
              : COMPANY_SPEND_BY_CATEGORY_QUERY,
          variables: {
            hgIds: companyId,
            per,
            ...filters,
          },
        };
      default:
        throw new Error(`Unsupported query type: ${queryType}`);
    }
  };

  const { query, variables } = getQueryConfig();

  const {
    data: queryData,
    loading,
    error,
  } = useQuery<SpendResponse>(query, {
    variables,
    context: {
      clientName: COMPANY_SEARCH_CLIENT,
    },
    fetchPolicy:
      queryType === SpendQueryType.AI_BY_CATEGORY ? "no-cache" : undefined,
    skip: !companyId || skip,
  });

  const spendData = queryData?.spend;
  const hasSpendData = (spendData?.count ?? 0) > 0;

  return { data: spendData, loading, error, hasSpendData };
};
